import type { Tone } from '../../ui/v2';
import type { DisplayStatus, GuaranteeMode, Onboarding, Stay } from './api';

/**
 * Labels and tones for the hotel pages. A stay's status is always read from
 * the API's `display_status`, never from `state`: a hold the PMS or our
 * expiry job released is `hold_expired`, though its state may be cancelled.
 */
export const STATUS: Record<
  DisplayStatus,
  { label: string; tone: Tone; hollow?: boolean }
> = {
  on_hold: { label: 'On hold', tone: 'warning', hollow: true },
  confirmed: { label: 'Confirmed', tone: 'success' },
  hold_expired: { label: 'Hold expired', tone: 'neutral', hollow: true },
  cancelled: { label: 'Cancelled', tone: 'danger' },
  in_house: { label: 'In house', tone: 'accent' },
  checked_out: { label: 'Checked out', tone: 'neutral' },
  inquiry: { label: 'Inquiry', tone: 'neutral', hollow: true },
};

export const statusOf = (status: string) =>
  STATUS[status as DisplayStatus] ?? { label: status, tone: 'neutral' };

export const SOURCE_LABEL: Record<string, string> = {
  voice: 'Phone (Arthur)',
  dashboard: 'Dashboard',
  sync: 'PMS',
};

export const GUARANTEE_LABEL: Record<GuaranteeMode, string> = {
  none: 'No guarantee',
  mews_payment_request: 'PMS payment request',
  stripe_link: 'Stripe payment link',
};

export const ACTION_LABEL: Record<string, string> = {
  quote: 'Quote',
  hold: 'Hold',
  confirm: 'Confirm',
  cancel: 'Cancel',
  change: 'Change dates',
  payment_request: 'Payment request',
};

/** The backend's own wording for each blocker code (hospitality rules, P4). */
const BLOCKER_MESSAGE: Record<string, string> = {
  no_pms_connection: "Connect the property's PMS first.",
  pms_connection_not_active: 'The PMS connection must be active.',
  no_pms_credential: 'Add the PMS access token first.',
  no_active_line: 'The property needs an active phone line.',
};

export const blockerMessage = (code: string) => BLOCKER_MESSAGE[code] ?? code;

export type ConnectionStatus = NonNullable<Onboarding['connection']>['status'];

/** Tone of a PMS connection status, shared by Settings and the Overview. */
export const CONNECTION_TONE: Record<ConnectionStatus, Tone> = {
  active: 'success',
  pending: 'warning',
  inactive: 'neutral',
  failed: 'danger',
};

/** "in_progress" → "In progress". */
export const sentence = (code: string) =>
  code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ');

export const guestName = (
  stay: Pick<Stay, 'guest_first_name' | 'guest_last_name'>,
) => [stay.guest_first_name, stay.guest_last_name].filter(Boolean).join(' ');

/** "Mon, Nov 3, 2026" for a local ISO date, with no zone shift. */
export function formatLocalDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function formatMoney(
  amount: string | null | undefined,
  currency: string,
): string {
  if (amount == null || amount === '') return '';
  const value = Number(amount);
  if (!Number.isFinite(value) || !currency) return amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${amount} ${currency}`;
  }
}

const PMS_ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Whether text holds a PMS GUID, which must not reach a URL. */
export const hasPmsId = (text: string) => PMS_ID.test(text);

/** The API's words when it refuses a search by PMS id. */
export const PMS_ID_SEARCH_MESSAGE =
  "Search by our reference, the confirmation number or the guest's name.";

export const HOTEL_BASE = '/hotel';

/**
 * The hotel user menu's Profile (IA Q3 (b)): the restaurant `/profile` page
 * rendered inside the hotel shell, so a hotel-only user keeps their sidebar.
 */
export const HOTEL_PROFILE_PATH = `${HOTEL_BASE}/profile`;

export const hotelPaths = {
  /** The property's landing page (Overview, IA §2). */
  overview: (propertyId: number) => `${HOTEL_BASE}/properties/${propertyId}`,
  stays: (propertyId: number) => `${HOTEL_BASE}/properties/${propertyId}/stays`,
  stay: (propertyId: number, stayRef: string) =>
    `${hotelPaths.stays(propertyId)}/${encodeURIComponent(stayRef)}`,
  guest: (propertyId: number, stayRef: string) =>
    `${hotelPaths.stay(propertyId, stayRef)}/guest`,
  settings: (propertyId: number) =>
    `${HOTEL_BASE}/properties/${propertyId}/settings`,
  /** The PMS connection (Mews): the hotel's one integration. */
  integrations: (propertyId: number) =>
    `${HOTEL_BASE}/properties/${propertyId}/integrations`,
};
